import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import SplitPanel from "../components/SplitPanel";
import { gradientForLighting } from "../utils/lightingGradients";
import { imageForLighting } from "../utils/lightingImages";
import StoreLinks from "../components/StoreLinks";
import Faq from "../components/Faq";
import Icon from "../components/Icon";
import { COMPANION } from "../config/app";
import { setPageMeta, breadcrumbLd } from "../utils/meta";



/**
 * One page about the companion app, deliberately not a set.
 *
 * Filtspot has its own site with its own device pages and its own guides
 * targeting "nd filter calculator" and "nd filter for <device>". If this
 * site published a cluster of app content it would compete with that for
 * the same queries — two properties owned by the same person asking Google
 * to pick one. This page exists to convert people already here and to give
 * internal links a destination, not to rank for terms the other site is
 * already built to win.
 */
export default function LightMeter() {
  useEffect(() => {
    setPageMeta({
      title: `Measuring the light, ${COMPANION.name} companion app`,
      description: "Every recipe here assumes a lighting category. A meter measures the light in front of you instead. What that difference is worth, and where each approach stops.",
      path: "/light-meter/",
      jsonLdId: "lightmeter-jsonld",
      jsonLd: {
        "@context": "https://schema.org",
        "@graph": [
          { "@type": "WebPage", name: "Measuring the light" },
          breadcrumbLd([{ name: "Home", path: "/" }, { name: "Measuring the light", path: "/light-meter/" }]),
        ],
      },
    });
  }, []);

  const faq = [
    {
      q: "Do I need the app to use this site?",
      a: "No. Every recipe and every reference page here is free, complete and public. The app answers a different question: what the light is doing right now. That's something a settings table structurally can't tell you.",
    },
    {
      q: "Why can't this website just measure the light?",
      a: "It can, roughly, and the on-device analyser on a scanned settings page does. But a browser only gets what the browser exposes: on some Android browsers that includes the camera's exposure settings, which is enough for an EV estimate, and on iOS Safari it includes none of it, which leaves only relative brightness. A native app reads the sensor directly and can calibrate against it.",
    },
    {
      q: "What does a confidence label mean?",
      a: "It tells you how the reading was arrived at: measured from the sensor, calibrated against a known reference, or estimated from conditions. Most tools in this space give you one number with no indication of how much to trust it, which is a problem when the answer changes what you buy or what you shoot.",
    },
    {
      q: "Is this site part of the app?",
      a: "They're separate. This site calculates settings from published specifications. The app measures conditions. They're useful together, and neither requires the other.",
    },
  ];

  return (
    <>
    <SplitPanel gradient={gradientForLighting("golden-hour")} image={imageForLighting("golden-hour")} imageText={{ eyebrow: "Companion app", title: "Measuring the light", sub: "Filtspot" }}>
    <main className="static-page light-meter-page split-article">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link> <span aria-hidden="true">/</span> <span>Measuring the light</span>
      </nav>

      <p className="eyebrow">Companion app</p>
      <h1>Every recipe here assumes a category. A meter measures the light.</h1>

      <p className="light-meter-lede">
        This site treats "full sun" as EV 15. That's the standard figure, and it's what every
        full-sun page is calculated from. It's also an assumption, and assumptions about light are wrong more often than people expect.
      </p>

      <h2>Where the assumption breaks</h2>
      <p>
        Thin high cloud takes a stop out of full sun without looking like it has. A pale wall behind
        you throws fill that a meter sees and your eye adjusts away. Four in the afternoon is not
        noon, but both are "sunny" in a dropdown. Each of those is a stop or two, and a stop or two
        is the difference between one filter and the next.
      </p>
      <p>
        The recipes here are still the right starting point. They get the frame rate, shutter, profile and white balance correct regardless of the exact light, and those are most of the decisions. What they can't do is know that today is half a stop down on the textbook figure.
      </p>

      <h2>What {COMPANION.name} does differently</h2>
      <ul className="light-meter-points">
        <li>
          <Icon name="light" />
          <div>
            <strong>Measures rather than assumes.</strong> A reading from the scene in front of you,
            not a category you picked from a list.
          </div>
        </li>
        <li>
          <Icon name="aperture" />
          <div>
            <strong>Knows your camera's real aperture.</strong> The ND answer depends on it, and a
            web page has to assume a value where an app can look it up.
          </div>
        </li>
        <li>
          <Icon name="iso" />
          <div>
            <strong>Labels how much to trust each reading.</strong> Measured, calibrated or
            estimated, so a rough number is never presented as a precise one.
          </div>
        </li>
      </ul>

      <div className="light-meter-cta">
        <StoreLinks />
        <p className="light-meter-cta-note">
          Or read more at <a href={COMPANION.siteUrl} rel="noopener">{COMPANION.siteUrl.replace(/^https?:\/\//, "")}</a>
        </p>
      </div>

      <h2>Being straight about the limits</h2>
      <p>
        No phone is a calibrated light meter, this app included. Phone camera sensors are built to
        produce a pleasing image, not an accurate measurement, and published research on using them
        for absolute lux puts the error meaningfully above what a dedicated meter achieves.
      </p>
      <p>
        That's the reason for the confidence labels rather than an argument against the app: a
        reading that tells you how certain it is beats a reading that doesn't, and both beat a
        category you guessed. If your work genuinely depends on precise absolute values, a physical
        incident meter is still the tool, and no software changes that.
      </p>

      <Faq items={faq} name="light-meter-faq" title="Questions" />

      <div data-prerender-ready="true" style={{ display: "none" }} />
    </main>
    </SplitPanel>
    <Footer />
    </>
  );
}
